<template>
  <div class="login-shell">
    <div class="login-card animate-in">
      <div class="logo-wrap logo-glass">
        <img src="/avenue44-logo.png" :alt="$t('label.app_name')">
      </div>

      <PrimeForm
          :resolver="resolver"
          @submit="login"
          class="form"
      >
        <Message
            v-if="infoMessage"
            severity="success"
            class="success-message"
        >
          {{ infoMessage }}
        </Message>

        <FormField name="email">
          <div class="field-group">
            <div class="floating-field">
              <InputText
                  v-model="form.email"
                  id="email"
                  type="text"
                  class="fancy-input"
                  placeholder=" "
              />
              <label for="email">{{ $t('label.email') }}</label>
            </div>

            <small
                v-if="errors.email"
                class="error-text"
            >
              {{ errors.email }}
            </small>
          </div>
        </FormField>

        <FormField name="password">
          <div class="field-group">
            <div class="floating-field">
              <InputText
                  v-model="form.password"
                  id="password"
                  type="password"
                  class="fancy-input"
                  placeholder=" "
              />
              <label for="password">{{ $t('label.password') }}</label>
            </div>

            <small
                v-if="errors.password"
                class="error-text"
            >
              {{ errors.password }}
            </small>
          </div>
        </FormField>

        <small
            v-if="errors.form"
            class="error-text text-center"
        >
          {{ errors.form }}
        </small>

        <small
            v-if="microsoftErrorMessage"
            class="error-text text-center"
        >
          {{ microsoftErrorMessage }}
        </small>

        <div class="actions">
          <Button
              link
              :label="$t('label.forgot_password')"
              class="forgot-link"
              @click="$router.push('/password-forgotten')"
          />
        </div>

        <Button
            type="submit"
            :label="$t('label.login')"
            class="login-button"
            :loading="loading"
        />

        <div class="sso-divider">
          <span>{{ $t('label.or_continue_with') }}</span>
        </div>

        <Button
            type="button"
            class="microsoft-button"
            @click="redirectToMicrosoftLogin"
        >
          <i class="fab fa-microsoft microsoft-icon"></i>
          <span>{{ $t('label.login_with_microsoft') }}</span>
        </Button>
      </PrimeForm>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import enhancedAxios from '../utils/axios.js';
import { Form as PrimeForm, FormField } from '@primevue/forms';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'LoginView',

  components: {
    PrimeForm,
    FormField,
  },

  data() {
    return {
      form: {
        email: '',
        password: '',
      },

      errors: {},
      loading: false,
      infoMessage: null,
      microsoftErrorMessage: null,
    };
  },

  computed: {
    ...mapGetters([
      'passwordResetInfoMessage',
    ]),

    resolver() {
      return ({ values }) => {
        const errors = {};

        if (!values.email) {
          errors.email = this.$t('validation.email_required');
        }

        if (!values.password) {
          errors.password = this.$t('validation.password_required');
        }

        return {
          values,
          errors,
        };
      };
    },
  },

  methods: {
    ...mapActions([
      'setUser',
      'resetPasswordResetInfoMessage',
    ]),
    ...mapActions('session', [
      'setAccessToken'
    ]),

    async login({ values, valid }) {
      this.errors = {};
      this.microsoftErrorMessage = null;

      if (!valid) {
        return;
      }

      this.loading = true;

      try {
        const authResponse = await axios.post(
            `${import.meta.env.VITE_API_ROOT}/api/auth/login`,
            {
              email: values.email,
              password: values.password,
            },
            { withCredentials: true }
        );

        const token = authResponse.data.token;
        const expiresAt = authResponse.data.refreshTokenExpiresAt
          ?? authResponse.data.expiresAt
          ?? authResponse.data.expires_at
          ?? null;

        await this.setAccessToken({ token, expiresAt });

        const meResponse = await enhancedAxios.get('/me');

        this.setUser(meResponse.data.user);

        this.$router.replace('/');
      } catch (error) {
        this.loading = false;

        if (error.response?.status === 401) {
          this.errors.form = error.response.data.message;

          return;
        }

        this.$toast.add({
          severity: 'error',
          summary: this.$t('toast.generic_error_try_again'),
        });
      }
    },

    redirectToMicrosoftLogin() {
      this.microsoftErrorMessage = null;

      globalThis.location.href = `${import.meta.env.VITE_API_ROOT}/auth/microsoft/redirect`;
    },

    resolveMicrosoftErrorMessage() {
      const microsoftErrorCode = this.$route.query.code;
      if (typeof microsoftErrorCode !== 'string' || microsoftErrorCode === '') {
        return null;
      }

      const translations = {
        microsoft_login_cancelled: this.$t('message.microsoft_sso_cancelled'),
        invalid_state: this.$t('message.microsoft_sso_invalid_state'),
        missing_code: this.$t('message.microsoft_sso_missing_code'),
        token_exchange_failed: this.$t('message.microsoft_sso_token_exchange_failed'),
        missing_email: this.$t('message.microsoft_sso_missing_email'),
        missing_subject: this.$t('message.microsoft_sso_missing_subject'),
        unknown_tenant: this.$t('message.microsoft_sso_unknown_tenant'),
        user_disabled: this.$t('message.microsoft_sso_user_disabled'),
        user_conflict: this.$t('message.microsoft_sso_user_conflict'),
        microsoft_sso_failed: this.$t('message.microsoft_sso_failed'),
      };

      return translations[microsoftErrorCode] ?? this.$t('message.microsoft_sso_failed');
    },
  },

  async mounted() {
    await this.$router.isReady();

    this.infoMessage = this.passwordResetInfoMessage;
    this.microsoftErrorMessage = this.resolveMicrosoftErrorMessage();

    this.resetPasswordResetInfoMessage();
  },
};
</script>


<style scoped>
.login-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
      radial-gradient(circle at 20% 20%, rgba(255,255,255,0.06), transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.04), transparent 40%),
      url('../../src/assets/images/background.png') center / cover no-repeat;
  position: relative;
}

.login-shell::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
}

.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: 3rem;
  border-radius: 24px;
  background: linear-gradient(
      180deg,
      rgba(255,255,255,0.14),
      rgba(255,255,255,0.06)
  );
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255,255,255,0.25);
  box-shadow:
      0 30px 80px rgba(0,0,0,0.55),
      inset 0 1px 0 rgba(255,255,255,0.25);
}

.animate-in {
  animation: cardIn 0.8s ease forwards;
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.logo-wrap {
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 2.5rem;
}

.logo-wrap::before {
  content: '';
  position: absolute;
  inset: -3em -5em -2.4em -5em;
  background:
      radial-gradient(
          circle at center,
          rgba(0,0,0,0.45),
          rgba(0,0,0,0.15) 60%,
          transparent 75%
      );
  border-radius: 22px;
  z-index: 0;
}

.logo-wrap img {
  position: relative;
  z-index: 1;
  max-height: 90px;
  filter:
      drop-shadow(0 4px 12px rgba(0,0,0,0.5))
      contrast(1.05)
      saturate(1.05);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.field-group {
  width: 100%;
}

.floating-field {
  position: relative;
}

.fancy-input {
  width: 100%;
  padding: 1.2rem 1rem;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 14px;
  color: white;
  font-size: 0.95rem;
  transition: all 0.25s ease;
}

.fancy-input:focus {
  outline: none;
  border-color: rgba(255,255,255,0.6);
  box-shadow: 0 0 0 3px rgba(255,255,255,0.15);
}

.floating-field label {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255,255,255,0.6);
  font-size: 0.85rem;
  pointer-events: none;
  transition: all 0.25s ease;
  background: transparent;
}

.fancy-input:focus + label,
.fancy-input:not(:placeholder-shown) + label {
  top: -8px;
  left: 0.8rem;
  padding: 0 6px;
  background: rgba(20,20,20,0.2);
  font-size: 0.7rem;
  color: white;
}

.login-button {
  margin-top: 1.5rem;
  padding: 0.9rem;
  border-radius: 16px;
  background: linear-gradient(
      135deg,
      #1a8b8f,
      #126064
  );
  border: none;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: white;
  transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      background 0.2s ease;
}

.login-button:hover {
  background: linear-gradient(
      135deg,
      #16767a,
      #0e4a4d
  );
  transform: translateY(-1px);
  box-shadow:
      0 12px 35px rgba(26,139,143,0.45);
}

.login-button:active {
  transform: translateY(0);
  box-shadow:
      0 6px 18px rgba(26,139,143,0.35);
}

.sso-divider {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  color: rgba(255,255,255,0.58);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.sso-divider::before,
.sso-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(
      90deg,
      rgba(255,255,255,0),
      rgba(255,255,255,0.3),
      rgba(255,255,255,0)
  );
}

.microsoft-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.95rem;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(12,18,26,0.72);
  color: #f8fafc;
  font-weight: 600;
  transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      border-color 0.2s ease,
      background 0.2s ease;
}

.microsoft-button:hover {
  transform: translateY(-1px);
  border-color: rgba(255,255,255,0.38);
  background: rgba(16,24,34,0.9);
  box-shadow: 0 16px 36px rgba(0,0,0,0.28);
}

.microsoft-icon {
  font-size: 1rem;
}

.forgot-link {
  padding: 0;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.7);
}

.error-text {
  font-size: 1rem;
  color: #f87171;
}

.success-message {
  background: rgba(34,197,94,0.15);
  border: 1px solid rgba(34,197,94,0.35);
  color: #4ade80;
}

</style>
